import { Button } from '@seen/ui'
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '@tabler/icons-react'
import type { Table } from '@tanstack/react-table'
import type { JSX } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
}

export function DataTablePagination<TData>({
  table,
}: DataTablePaginationProps<TData>): JSX.Element {
  return (
    <div className="flex items-center justify-between border-t p-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {table.getFilteredSelectedRowModel().rows.length} of{' '}
        {table.getFilteredRowModel().rows.length} row(s) selected.
      </div>
      <div className="space-x-6 flex items-center lg:space-x-8">
        <div className="space-x-2 flex items-center">
          <p className="text-sm font-medium">Rows per page</p>
          <Combobox
            options={[
              { label: '10', value: '10', keywords: ['10'] },
              { label: '20', value: '20', keywords: ['20'] },
              { label: '25', value: '25', keywords: ['25'] },
              { label: '30', value: '30', keywords: ['30'] },
              { label: '40', value: '40', keywords: ['40'] },
              { label: '50', value: '50', keywords: ['50'] },
            ]}
            onSelect={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <Button variant="outline" size="sm">
              {table.getState().pagination.pageSize}
            </Button>
          </Combobox>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of{' '}
          {table.getPageCount()}
        </div>
        <div className="space-x-2 flex items-center">
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <IconChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <IconChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <IconChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <IconChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}
